import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Briefcase, FileText, MessageSquare, Star, CreditCard, User, 
  Plus, MapPin, Clock, Eye, CheckCircle2, XCircle, AlertCircle, 
  Sparkles, HardHat, Building2, ArrowLeftRight, FolderKanban, BarChart3,
  Heart, Shield
, Newspaper } from 'lucide-react';
import { SubscriptionStatusCard } from '../components/dashboard/SubscriptionStatusCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { t } = useTranslation();
  const { user, isCompany, isContractor, isPremium, refreshUser } = useAuth();
  const [myJobs, setMyJobs] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [roleCompany, setRoleCompany] = useState(false);
  const [roleContractor, setRoleContractor] = useState(false);
  const [savingRoles, setSavingRoles] = useState(false);

  useEffect(() => {
    if (user) {
      setRoleCompany(user.user_type === 'company' || user.user_type === 'both');
      setRoleContractor(user.user_type === 'contractor' || user.user_type === 'both');
    }
  }, [user]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [msgRes] = await Promise.all([
          axios.get(`${API}/messages/unread/count`, { withCredentials: true })
        ]);
        setUnreadMessages(msgRes.data.count || 0);

        if (isCompany) {
          const jobsRes = await axios.get(`${API}/my-jobs`, { withCredentials: true });
          setMyJobs(jobsRes.data || []);
        }

        if (isContractor) {
          const appsRes = await axios.get(`${API}/my-applications`, { withCredentials: true });
          setMyApplications(appsRes.data || []);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [isCompany, isContractor]);

  const toggleRole = async (role) => {
    const newCompany = role === 'company' ? !roleCompany : roleCompany;
    const newContractor = role === 'contractor' ? !roleContractor : roleContractor;

    if (!newCompany && !newContractor) {
      toast.error('Au moins un rôle doit être actif');
      return;
    }

    setSavingRoles(true);
    try {
      await axios.put(`${API}/users/me/roles`, {
        is_company: newCompany,
        is_contractor: newContractor
      }, { withCredentials: true });
      setRoleCompany(newCompany);
      setRoleContractor(newContractor);
      toast.success('Rôle mis à jour');
      await refreshUser();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSavingRoles(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted': return <Badge className="bg-emerald-100 text-emerald-700">Acceptée</Badge>;
      case 'rejected': return <Badge className="bg-red-100 text-red-700">Refusée</Badge>;
      default: return <Badge className="bg-yellow-100 text-yellow-700">En attente</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A8A]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header + Role Switcher */}
        <div className="flex flex-col gap-4 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="font-heading font-bold text-3xl text-[#111827]" data-testid="dashboard-title">
                {t('dashboard.title')}
              </h1>
              <p className="text-[#4B5563] mt-1">Bienvenue, {user?.company_name}</p>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
              <Link to="/dashboard/actualites">
                <Button variant="outline" data-testid="dashboard-news-link">
                  <Newspaper className="w-4 h-4 mr-2 text-[#1E3A8A]" />
                  Actualités du bâtiment
                </Button>
              </Link>
              {isCompany && (
                <Link to="/dashboard/post-job">
                  <Button className="btn-accent" data-testid="post-job-button">
                    <Plus className="w-5 h-5 mr-2" />
                    {t('jobs.postJob')}
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Profile Completion Bar */}
          {(() => {
            const fields = [
              { name: 'Entreprise', filled: !!user?.company_name },
              { name: 'Ville', filled: !!user?.city },
              { name: 'Téléphone', filled: !!user?.phone },
              { name: 'TVA/SIRET', filled: !!(user?.vat_number?.trim()) },
              { name: 'Corps de métier', filled: !!(user?.trades?.length) },
              { name: 'Description', filled: !!user?.description },
            ];
            const filled = fields.filter(f => f.filled).length;
            const pct = Math.round((filled / fields.length) * 100);
            const missing = fields.filter(f => !f.filled);
            return pct < 100 ? (
              <Card className="border-2 border-[#F59E0B]/30 bg-[#F59E0B]/5" data-testid="profile-completion-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-[#374151]">Profil complété à {pct}%</p>
                    <Link to="/dashboard/profile">
                      <Button variant="outline" size="sm" className="text-xs">Compléter</Button>
                    </Link>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                    <div className="h-full bg-[#F59E0B] rounded-full transition-all" style={{width: `${pct}%`}} />
                  </div>
                  <p className="text-xs text-[#6B7280]">
                    Manquant : {missing.map(m => m.name).join(', ')}
                  </p>
                </CardContent>
              </Card>
            ) : null;
          })()}

          {/* Role Toggles - Green/Red buttons */}
          <Card className="border-2 border-[#1E3A8A]/10" data-testid="role-toggles-card">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-[#374151] mb-3">Mes rôles sur la plateforme</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => toggleRole('company')}
                  disabled={savingRoles}
                  className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                    roleCompany
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-red-300 bg-red-50'
                  }`}
                  data-testid="toggle-company-btn"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    roleCompany ? 'bg-emerald-500' : 'bg-red-400'
                  }`}>
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-semibold text-sm text-[#111827]">Donneur d'ordre</p>
                    <p className="text-xs text-[#6B7280]">Publier des chantiers</p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                    roleCompany
                      ? 'bg-emerald-500 text-white'
                      : 'bg-red-400 text-white'
                  }`}>
                    {roleCompany ? 'ACTIF' : 'INACTIF'}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => toggleRole('contractor')}
                  disabled={savingRoles}
                  className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                    roleContractor
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-red-300 bg-red-50'
                  }`}
                  data-testid="toggle-contractor-btn"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    roleContractor ? 'bg-emerald-500' : 'bg-red-400'
                  }`}>
                    <HardHat className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-semibold text-sm text-[#111827]">Sous-traitant</p>
                    <p className="text-xs text-[#6B7280]">Postuler aux chantiers</p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                    roleContractor
                      ? 'bg-emerald-500 text-white'
                      : 'bg-red-400 text-white'
                  }`}>
                    {roleContractor ? 'ACTIF' : 'INACTIF'}
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-surface" data-testid="stat-jobs-or-apps">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#1E3A8A]/10 rounded-full flex items-center justify-center">
                  {isCompany ? <Briefcase className="w-6 h-6 text-[#1E3A8A]" /> : <FileText className="w-6 h-6 text-[#1E3A8A]" />}
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#111827]">
                    {isCompany ? myJobs.length : myApplications.length}
                  </p>
                  <p className="text-sm text-[#4B5563]">
                    {isCompany ? 'Chantiers publiés' : 'Candidatures'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-surface" data-testid="stat-messages">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F59E0B]/10 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#111827]">{unreadMessages}</p>
                  <p className="text-sm text-[#4B5563]">Messages non lus</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-surface" data-testid="stat-rating">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Star className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#111827]">{user?.rating?.toFixed(1) || '0.0'}</p>
                  <p className="text-sm text-[#4B5563]">Note moyenne</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-surface" data-testid="stat-subscription">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#111827] capitalize">{user?.subscription_plan || 'free'}</p>
                  <p className="text-sm text-[#4B5563]">Abonnement</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mb-8">
          <SubscriptionStatusCard />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Link to="/dashboard/profile">
            <Card className="card-surface cursor-pointer hover:border-[#1E3A8A]/30" data-testid="action-profile">
              <CardContent className="p-4 flex items-center gap-3">
                <User className="w-5 h-5 text-[#1E3A8A]" />
                <span className="font-medium">{t('nav.profile')}</span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/dashboard/messages">
            <Card className="card-surface cursor-pointer hover:border-[#1E3A8A]/30" data-testid="action-messages">
              <CardContent className="p-4 flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-[#1E3A8A]" />
                <span className="font-medium">{t('nav.messages')}</span>
                {unreadMessages > 0 && (
                  <Badge className="bg-red-500 text-white ml-auto">{unreadMessages}</Badge>
                )}
              </CardContent>
            </Card>
          </Link>
          <Link to="/dashboard/projects">
            <Card className="card-surface cursor-pointer hover:border-[#1E3A8A]/30" data-testid="action-projects">
              <CardContent className="p-4 flex items-center gap-3">
                <FolderKanban className="w-5 h-5 text-[#1E3A8A]" />
                <span className="font-medium">Projets</span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/dashboard/analytics">
            <Card className="card-surface cursor-pointer hover:border-[#1E3A8A]/30" data-testid="action-analytics">
              <CardContent className="p-4 flex items-center gap-3">
                <BarChart3 className="w-5 h-5 text-[#1E3A8A]" />
                <span className="font-medium">Analytics</span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/dashboard/favorites">
            <Card className="card-surface cursor-pointer hover:border-[#1E3A8A]/30" data-testid="action-favorites">
              <CardContent className="p-4 flex items-center gap-3">
                <Heart className="w-5 h-5 text-red-400" />
                <span className="font-medium">Favoris</span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/dashboard/insurance">
            <Card className="card-surface cursor-pointer hover:border-[#1E3A8A]/30" data-testid="action-insurance">
              <CardContent className="p-4 flex items-center gap-3">
                <Shield className="w-5 h-5 text-emerald-500" />
                <span className="font-medium">Assurances</span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/dashboard/subscription">
            <Card className="card-surface cursor-pointer hover:border-[#1E3A8A]/30" data-testid="action-subscription">
              <CardContent className="p-4 flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-[#1E3A8A]" />
                <span className="font-medium">{t('dashboard.subscription')}</span>
              </CardContent>
            </Card>
          </Link>
          <Link to={isCompany ? "/contractors" : "/jobs"}>
            <Card className="card-surface cursor-pointer hover:border-[#1E3A8A]/30" data-testid="action-browse">
              <CardContent className="p-4 flex items-center gap-3">
                <Eye className="w-5 h-5 text-[#1E3A8A]" />
                <span className="font-medium">{isCompany ? 'Sous-traitants' : 'Chantiers'}</span>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Main Content */}
        <Tabs defaultValue={isCompany ? "jobs" : "applications"} key={user?.user_type}>
          <TabsList className="bg-white border border-[#1E3A8A]/10 mb-6">
            {isCompany && <TabsTrigger value="jobs" data-testid="tab-jobs">{t('dashboard.myJobs')}</TabsTrigger>}
            {isContractor && <TabsTrigger value="applications" data-testid="tab-applications">{t('dashboard.myApplications')}</TabsTrigger>}
          </TabsList>

          {isCompany && (
            <TabsContent value="jobs">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">{t('dashboard.myJobs')}</CardTitle>
                  {!isPremium && (() => {
                    const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
                    const used = myJobs.filter((j) => new Date(j.created_at) >= monthStart).length;
                    return (
                      <p className="text-sm text-[#6B7280]" data-testid="free-jobs-quota">
                        Offre gratuite : <strong>{used}/3</strong> publication{used > 1 ? 's' : ''} utilisée{used > 1 ? 's' : ''} ce mois-ci (remise à zéro le 1er du mois). Passez Premium pour publier sans limite.
                      </p>
                    );
                  })()}
                </CardHeader>
                <CardContent>
                  {myJobs.length > 0 ? (
                    <div className="space-y-4">
                      {myJobs.map((job) => (
                        <div key={job.id} className="flex items-center justify-between p-4 bg-[#F9FAFB] rounded-md" data-testid={`job-item-${job.id}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{job.title}</h4>
                              <Badge className={job.status === 'open' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                                {job.status === 'open' ? 'Ouvert' : 'Fermé'}
                              </Badge>
                              {job.is_urgent && <Badge className="badge-urgent">Urgent</Badge>}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-[#4B5563]">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {job.city}
                              </span>
                              <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" /> {job.applications_count || 0} candidatures
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Link to={`/dashboard/jobs/${job.id}/matching`}>
                              <Button variant="outline" size="sm" className="gap-1">
                                <Sparkles className="w-3 h-3 text-[#F59E0B]" />
                                IA
                              </Button>
                            </Link>
                            <Link to={`/jobs/${job.id}`}>
                              <Button variant="outline" size="sm">Voir</Button>
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-[#4B5563] mb-4">Vous n'avez pas encore publié de chantier</p>
                      <Link to="/dashboard/post-job">
                        <Button className="btn-primary">
                          <Plus className="w-4 h-4 mr-2" />
                          Publier un chantier
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {isContractor && (
            <TabsContent value="applications">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">{t('dashboard.myApplications')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {myApplications.length > 0 ? (
                    <div className="space-y-3">
                      {myApplications.map((app) => (
                        <div key={app.id} className="flex items-center justify-between p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]" data-testid={`app-item-${app.id}`}>
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            {app.status === 'accepted' ? (
                              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-testid={`app-status-icon-${app.id}`}>
                                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                              </div>
                            ) : app.status === 'rejected' ? (
                              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" data-testid={`app-status-icon-${app.id}`}>
                                <XCircle className="w-5 h-5 text-red-600" />
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0" data-testid={`app-status-icon-${app.id}`}>
                                <AlertCircle className="w-5 h-5 text-yellow-600" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <h4 className="font-semibold text-[#111827] truncate">{app.job_title || `Chantier #${app.job_id?.slice(-6)}`}</h4>
                              <div className="flex items-center gap-3 text-sm text-[#6B7280] mt-0.5">
                                {app.job_city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{app.job_city}</span>}
                                {app.job_company_name && <span>{app.job_company_name}</span>}
                                <span>{new Date(app.created_at).toLocaleDateString('fr-FR')}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            {app.status === 'accepted' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 font-semibold" data-testid={`app-badge-${app.id}`}>Engagé</Badge>
                            ) : app.status === 'rejected' ? (
                              <Badge className="bg-red-100 text-red-700 border border-red-300 font-semibold" data-testid={`app-badge-${app.id}`}>Refusé</Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-300 font-semibold" data-testid={`app-badge-${app.id}`}>En attente</Badge>
                            )}
                            <Link to={`/jobs/${app.job_id}`}>
                              <Button variant="outline" size="sm" data-testid={`app-view-${app.id}`}>Voir</Button>
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-[#4B5563] mb-4">Vous n'avez pas encore postulé à un chantier</p>
                      <Link to="/jobs">
                        <Button className="btn-primary">
                          Voir les chantiers disponibles
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default DashboardPage;
